/**
 * InvitationCodeDetailPage
 * Detail page for invitation codes with usage history
 */

import { useParams, useNavigate } from "react-router-dom";
import { Ban, Users, Clock, Loader2, Copy } from "lucide-react";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useInvitationCode,
  useRevokeInvitationCode,
} from "@/hooks/useInvitationCodeQueries";
import { formatDate, formatDateTime } from "@/lib/utils";
import { toast } from "sonner";
import { useState } from "react";

export function InvitationCodeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: code, isLoading, error } = useInvitationCode(id!);
  const revokeMutation = useRevokeInvitationCode();

  const [revokeDialog, setRevokeDialog] = useState(false);

  if (!id) {
    return (
      <div className="space-y-6">
        <div className="py-12 text-center">
          <p className="text-destructive-ink">Invalid invitation code ID</p>
          <Button
            onClick={() => navigate("/admin/invitation-codes")}
            className="mt-4"
          >
            Back to Invitation Codes
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Invitation Code Details"
          subtitle="Loading invitation code information"
          back={{
            label: "Back to Invitation Codes",
            onClick: () => navigate("/admin/invitation-codes"),
          }}
        />
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">Loading...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !code) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Invitation Code Details"
          subtitle="Invitation code information"
          back={{
            label: "Back to Invitation Codes",
            onClick: () => navigate("/admin/invitation-codes"),
          }}
        />
        <Card>
          <CardContent className="pt-6">
            <div className="py-12 text-center">
              <p className="text-destructive-ink">
                Failed to load invitation code
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {error?.message || "Invitation code not found"}
              </p>
              <Button
                onClick={() => navigate("/admin/invitation-codes")}
                className="mt-4"
              >
                Back to Invitation Codes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(code.code);
    toast.success("Code copied to clipboard");
  };

  const handleRevoke = () => {
    revokeMutation.mutate(id, {
      onSuccess: () => {
        setRevokeDialog(false);
      },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "default";
      case "Paused":
        return "secondary";
      case "Expired":
        return "outline";
      default:
        return "outline";
    }
  };

  const remaining =
    code.maxUses != null ? Math.max(0, code.maxUses - code.usageCount) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={code.code}
        titleClassName="font-mono"
        subtitle="Invitation code details and usage history"
        back={{
          label: "Back to Invitation Codes",
          onClick: () => navigate("/admin/invitation-codes"),
        }}
        titleAdornment={
          <Badge variant={getStatusColor(code.status)}>{code.status}</Badge>
        }
        actions={[
          {
            label: "Copy",
            onClick: handleCopy,
            variant: "outline",
            icon: <Copy className="mr-2 h-4 w-4" />,
          },
          ...(code.status === "Active"
            ? [
                {
                  label: "Revoke",
                  onClick: () => setRevokeDialog(true),
                  variant: "destructive" as const,
                  icon: <Ban className="mr-2 h-4 w-4" />,
                },
              ]
            : []),
        ]}
      />

      {/* Code Details */}
      <Card>
        <CardHeader>
          <CardTitle>Code Details</CardTitle>
          <CardDescription>
            Complete information about this invitation code
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Code
              </dt>
              <dd className="mt-1 text-sm font-mono">{code.code}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Status
              </dt>
              <dd className="mt-1 text-sm">
                <Badge variant={getStatusColor(code.status)}>
                  {code.status}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Reward
              </dt>
              <dd className="mt-1 text-sm">
                {new Intl.NumberFormat("vi-VN").format(code.rewardAmount)}{" "}
                {code.currency}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Usage
              </dt>
              <dd className="mt-1 text-sm">
                {code.usageCount} / {code.maxUses ?? "∞"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Created At
              </dt>
              <dd className="mt-1 text-sm">{formatDate(code.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                Expires
              </dt>
              <dd className="mt-1 text-sm">
                {code.validTo ? formatDate(code.validTo) : "Never"}
              </dd>
            </div>
            {code.description && (
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-sm font-medium text-muted-foreground">
                  Description
                </dt>
                <dd className="mt-1 text-sm">{code.description}</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Usage Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Uses</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{code.usageCount}</div>
            <p className="text-xs text-muted-foreground">Total times used</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Max Uses</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{code.maxUses ?? "∞"}</div>
            <p className="text-xs text-muted-foreground">
              {code.maxUses ? "Allowed uses" : "Unlimited"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Remaining</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {remaining !== null ? remaining : "∞"}
            </div>
            <p className="text-xs text-muted-foreground">Uses remaining</p>
          </CardContent>
        </Card>
      </div>

      {/* Usage History */}
      <Card>
        <CardHeader>
          <CardTitle>Usage History ({code.usages?.length || 0})</CardTitle>
          <CardDescription>
            Users who claimed this invitation code
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!code.usages || code.usages.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-sm font-medium text-muted-foreground">
                No usage history yet
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {code.status === "Active"
                  ? "This code has not been used yet"
                  : "This code was never used"}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User ID</TableHead>
                    <TableHead>Tenant ID</TableHead>
                    <TableHead>Reward</TableHead>
                    <TableHead>Claimed At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {code.usages.map((usage) => (
                    <TableRow key={usage.id}>
                      <TableCell className="font-mono text-xs">
                        {usage.userId}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {usage.tenantId}
                      </TableCell>
                      <TableCell>
                        {new Intl.NumberFormat("vi-VN").format(
                          usage.rewardAmount,
                        )}{" "}
                        {usage.currency}
                      </TableCell>
                      <TableCell>{formatDateTime(usage.claimedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={revokeDialog} onOpenChange={setRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation Code</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to revoke invitation code{" "}
              <strong>{code.code}</strong>? This action cannot be undone and the
              code will no longer be valid for registration.
              {code.usageCount > 0 && (
                <span className="mt-2 block text-destructive-ink">
                  Warning: This code has already been used by {code.usageCount}{" "}
                  user(s). Revoking will not affect existing accounts.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? "Revoking..." : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
